"use client";

import React, { useState } from "react";
import { MapPin, Zap, Clock, Navigation, ShieldCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const serviceAreas = [{
  id: "richmond",
  name: "Richmond, TX",
  x: 35,
  y: 65,
  desc: "Operational Headquarters & Local Dispatch"
}, {
  id: "sugar-land",
  name: "Sugar Land, TX",
  x: 55,
  y: 55,
  desc: "Residential Panel Upgrades & EV Charging"
}, {
  id: "missouri-city",
  name: "Missouri City, TX",
  x: 70,
  y: 60,
  desc: "Priority Commercial Electrical Solutions"
}, {
  id: "houston",
  name: "Greater Houston",
  x: 65,
  y: 35,
  desc: "Extended Service for Major Installations"
}];
export default function WhereWeWork() {
  const [activeArea, setActiveArea] = useState<string | null>(null);
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: mapRef,
    isVisible: mapVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  return <section data-section-id="174494" id="where-we-keep-the-lights-on" className="relative py-24 bg-background dark overflow-hidden" ref={sectionRef}>
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.05] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:4rem_4rem]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {}
          <div className="lg:col-span-5 space-y-8">
            <div className={cn("space-y-4 transition-all duration-700 transform", sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-primary/10 text-primary font-mono text-xs font-bold tracking-widest uppercase border border-primary/20">
                <Navigation className="size-4" />
                Operational Radius
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold text-foreground tracking-tighter font-sans uppercase italic">
                Local Presence, <br />
                <span className="text-primary">Texas Tough Service.</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed font-sans">
                Humberg Electrical Services, LLC is family-owned and operated in Richmond, TX. We bring precision electrical solutions to Fort Bend County and the surrounding Houston metro area since 2013.
              </p>
            </div>

            <div className="space-y-3">
              {serviceAreas.map((area, i) => <div key={area.id} data-index={i} onMouseEnter={() => setActiveArea(area.id)} onMouseLeave={() => setActiveArea(null)} className={cn("group relative p-4 rounded-sm border transition-all duration-300 cursor-pointer overflow-hidden", activeArea === area.id ? "border-primary bg-primary/5 shadow-lg shadow-primary/5" : "border-border/50 bg-card hover:border-primary/50 hover:shadow-sm")} style={{
              transitionDelay: `${i * 50}ms`,
              opacity: sectionVisible ? 1 : 0,
              transform: sectionVisible ? "translateY(0)" : "translateY(10px)"
            }}>
                  {}
                  <div className={cn("absolute left-0 top-0 bottom-0 w-1 bg-primary transition-transform duration-300 origin-top", activeArea === area.id ? "scale-y-100" : "scale-y-0")} />
                  
                  <div className="flex items-start gap-4 relative z-10">
                    <div className={cn("mt-1 p-2 rounded-sm transition-colors duration-300", activeArea === area.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary")}>
                      <MapPin className="size-5" />
                    </div>
                    <div>
                      <h3 className={cn("font-bold text-lg transition-colors duration-300 font-sans tracking-tight", activeArea === area.id ? "text-foreground" : "text-foreground/80")}>
                        {area.name}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1 font-sans">
                        {area.desc}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>
            
            <div className={cn("flex items-center gap-3 text-sm text-muted-foreground font-bold font-mono tracking-wide uppercase transition-opacity duration-700 delay-300", sectionVisible ? "opacity-100" : "opacity-0")}>
              <ShieldCheck className="size-5 text-primary" />
              Licensed & Insured • TECL #30219
            </div>
          </div>

          {}
          <div ref={mapRef} className="lg:col-span-7 relative">
            <div className={cn("relative w-full aspect-square md:aspect-[4/3] lg:aspect-[16/11] bg-card rounded-sm overflow-hidden shadow-2xl transition-all duration-1000 transform border border-border", mapVisible ? "opacity-100 scale-100" : "opacity-0 scale-95")}>
              {}
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:2rem_2rem]" />
              
              {}
              <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 100 100" preserveAspectRatio="none">
                <path d="M 0 40 Q 30 30, 50 60 T 100 50" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-primary" />
                <path d="M 20 0 Q 40 50, 80 100" fill="none" stroke="currentColor" strokeWidth="0.2" className="text-muted-foreground" />
                <path d="M 0 70 Q 50 80, 100 20" fill="none" stroke="currentColor" strokeWidth="0.3" className="text-muted-foreground" />
                <circle cx="35" cy="65" r="15" fill="none" stroke="currentColor" strokeWidth="0.1" className="text-primary" strokeDasharray="1 2" />
                <circle cx="65" cy="35" r="25" fill="none" stroke="currentColor" strokeWidth="0.1" className="text-primary" strokeDasharray="1 2" />
              </svg>

              {}
              {serviceAreas.map((area, i) => {
              const isActive = activeArea === area.id;
              return <div key={area.id} data-index={i} className="absolute transform -translate-x-1/2 -translate-y-1/2 z-20" style={{
                left: `${area.x}%`,
                top: `${area.y}%`
              }}>
                    {}
                    <div className={cn("absolute inset-0 rounded-full bg-primary origin-center transition-all", isActive ? "animate-ping opacity-60 duration-1000" : "animate-ping opacity-20 duration-[3000ms]")} />
                    
                    {}
                    <div className={cn("relative rounded-full bg-primary border-[3px] border-card shadow-[0_0_25px_rgba(var(--color-primary),0.8)] transition-all duration-300 cursor-pointer", isActive ? "size-6 scale-110" : "size-4 hover:scale-125 hover:bg-primary/90")} onMouseEnter={() => setActiveArea(area.id)} onMouseLeave={() => setActiveArea(null)} />

                    {}
                    <div className={cn("absolute top-full left-1/2 -translate-x-1/2 mt-3 px-3 py-1.5 bg-background/95 backdrop-blur-md border border-border text-foreground text-xs font-black rounded-sm shadow-2xl whitespace-nowrap transition-all duration-300 pointer-events-none tracking-tight", isActive ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2")}>
                      {area.name.toUpperCase()}
                      {}
                      <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-background border-t border-l border-border rotate-45" />
                    </div>
                  </div>;
            })}

              {}
              <Card className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 w-[calc(100%-2rem)] sm:w-80 bg-background/95 backdrop-blur-xl border-primary/30 shadow-[0_0_40px_rgba(0,0,0,0.5)] z-30 rounded-sm">
                <CardContent className="p-4 sm:p-5 flex items-center gap-4">
                  <div className="relative">
                    <div className="absolute inset-0 bg-destructive/20 rounded-full animate-pulse" />
                    <div className="relative size-12 rounded-full bg-destructive/10 flex items-center justify-center shrink-0 border border-destructive/30">
                      <Zap className="size-6 text-destructive drop-shadow-[0_0_12px_rgba(239,68,68,0.6)]" />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-black font-mono uppercase tracking-[0.2em] mb-1">
                      <Clock className="size-3.5 text-destructive" />
                      24/7 Response
                    </div>
                    <p className="text-lg sm:text-2xl font-extrabold text-foreground leading-none font-sans tracking-tighter">
                      832-405-3766
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              {}
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent pointer-events-none" />
            </div>
            
            {}
            <div className="absolute -inset-4 bg-primary/5 blur-3xl -z-10 rounded-full" />
          </div>

        </div>
      </div>
    </section>;
}